import api from '@dashboard/services/axiosApi'
import { useAuthStore } from '@dashboard/stores/authStore'
import { useConfigStore } from '@dashboard/stores/configStore'
import type { RouteLocationNormalized } from 'vue-router'

export const AnalyticsEventTypes = Object.freeze({
  // User Authentication & Onboarding
  // REGISTERED: 'Completed signup user details',
  LOGGED_IN: 'Logged in',
  LOGGED_OUT: 'Logged out',
  //ACCESSED_TEAM: 'Accessed team',
  //TEAM_SWITCHED: 'Team switched',

  // Creation Events
  CAMPAIGN_CREATED: 'Campaign created',
  EMAIL_CREATED: 'Email created',
  GOAL_CREATED: 'Goal created',
  TAG_CREATED: 'Tag created',
  SEGMENT_GROUP_CREATED: 'Segment group created',
  SEGMENT_CREATED: 'Segment created',
  QUESTION_CREATED: 'Question created',
  RIGHT_ASK_QUESTION_CREATED: 'RightAsk Question created',
  FLOW_CREATED: 'Flow created',
  WIDGET_CREATED: 'Widget created',
  OFFER_CREATED: 'Offer created',
  FORM_CREATED: 'Form created',
  INTEGRATION_CREATED: 'Integration created',
  PHRASE_CREATED: 'Phrase created',
  ATTRIBUTE_CREATED: 'Attribute created',
  TEAM_CREATED: 'Team created',

  // Update Events
  CAMPAIGN_UPDATED: 'Campaign updated',
  GOAL_UPDATED: 'Goal updated',
  SEGMENT_GROUP_UPDATED: 'Segment group updated',
  SEGMENT_UPDATED: 'Segment updated',
  QUESTION_UPDATED: 'Question updated',
  FLOW_UPDATED: 'Flow updated',
  WIDGET_UPDATED: 'Widget updated',
  OFFER_UPDATED: 'Offer updated',
  INTEGRATION_UPDATED: 'Integration updated',
  SETTINGS_UPDATED: 'Settings updated',
  PROFILE_UPDATED: 'Profile updated',
  TEAM_UPDATED: 'Team updated',

  // Delete Events
  CAMPAIGN_DELETED: 'Campaign deleted',
  EMAIL_DELETED: 'Email deleted',
  GOAL_DELETED: 'Goal deleted',
  SEGMENT_GROUP_DELETED: 'Segment group deleted',
  SEGMENT_DELETED: 'Segment deleted',
  QUESTION_DELETED: 'Question deleted',
  FLOW_DELETED: 'Flow deleted',
  WIDGET_DELETED: 'Widget deleted',
  OFFER_DELETED: 'Offer deleted',
  INTEGRATION_DELETED: 'Integration deleted',
  TEAM_DELETED: 'Team deleted',

  // Team Management Events
  TEAM_MEMBER_ADDED: 'Team member added',
  TEAM_MEMBER_REMOVED: 'Team member removed',
  TEAM_MEMBER_UPDATED: 'Team member updated',

  // View/Navigation Events
  DASHBOARD_VIEWED: 'Dashboard viewed',
  AGENT_ONBOARDING_PROMPT_COPIED: 'Agent onboarding prompt copied',
  AGENT_ONBOARDING_DISMISSED: 'Agent onboarding dismissed',
  PROMOTION_CTA_CLICKED: 'Promotion CTA clicked',
  PROMOTION_DISMISSED: 'Promotion dismissed',
  GOAL_SUGGESTION_ACCEPTED: 'Goal suggestion accepted',
  GOAL_SUGGESTION_DISMISSED: 'Goal suggestion dismissed',
  REVENUE_CONNECT_STARTED: 'Revenue connect started',
  REVENUE_CONNECTED: 'Revenue connected',
  RECOMMENDATION_BUILD_CLICKED: 'Recommendation build clicked',
  RECOMMENDATION_UPGRADE_CLICKED: 'Recommendation upgrade clicked',
  RECOMMENDATION_NOT_NOW: 'Recommendation not now',
  RECOMMENDATION_DISMISSED: 'Recommendation dismissed',
  ATTENTION_ACTION_CLICKED: 'Attention action clicked',
  SETUP_STEP_COMPLETED: 'Setup step completed',
  VIEWED_FEED: 'Viewed feed',
  VIEWED_LEADS_FEED: 'Viewed leads feed',
  VIEWED_SEGMENT_GROUPS: 'Viewed segment groups',
  VIEWED_SEGMENT_GROUP_DETAILS: 'Viewed segment group details',
  VIEWED_QUESTIONS: 'Viewed questions',
  VIEWED_QUESTION_DETAILS: 'Viewed question details',
  VIEWED_CAMPAIGNS: 'Viewed campaigns',
  VIEWED_CAMPAIGN_DETAILS: 'Viewed campaign details',
  VIEWED_SEGMENTS: 'Viewed segments',
  VIEWED_SEGMENT_DETAILS: 'Viewed segment details',
  VIEWED_FLOWS: 'Viewed flows',
  VIEWED_FLOW_BUILDER: 'Viewed flow builder',
  VIEWED_WIDGETS: 'Viewed widgets',
  VIEWED_WIDGET_DETAILS: 'Viewed widget details',
  VIEWED_OFFERS: 'Viewed offers',
  VIEWED_OFFER_DETAILS: 'Viewed offer details',
  VIEWED_FORMS: 'Viewed forms',
  VIEWED_FORM_DETAILS: 'Viewed form details',
  VIEWED_EMAILS: 'Viewed emails',
  VIEWED_EMAIL_DETAILS: 'Viewed email details',
  VIEWED_GOALS: 'Viewed goals',
  VIEWED_GOAL_DETAILS: 'Viewed goal details',
  VIEWED_INTEGRATIONS: 'Viewed integrations',
  VIEWED_INTEGRATION_DETAILS: 'Viewed integration details',
  VIEWED_INSIGHTS: 'Viewed insights',
  VIEWED_ANALYTICS: 'Viewed analytics',
  VIEWED_SETTINGS: 'Viewed settings',
  VIEWED_TEAM_SETTINGS: 'Viewed team settings',
  VIEWED_PROFILE: 'Viewed profile',
  VIEWED_BILLING: 'Viewed billing',
  VIEWED_PERFECT_PAGE: 'Viewed perfect page',
  VIEWED_TEMPLATES: 'Viewed templates',
  VIEWED_TEMPLATE_DETAILS: 'Viewed template details',
  VIEWED_HELP: 'Viewed help',

  // Activation Milestones
  OUTCOME_CREATE_ENTRY_OPENED: 'Outcome create entry opened',
  INTEGRATION_CONNECTED: 'Integration connected',
  INTEGRATION_DISCONNECTED: 'Integration disconnected',
  VIEWED_ONBOARDING: 'Viewed onboarding',
  ONBOARDING_OUTCOME_SELECTED: 'Onboarding outcome selected',
  ONBOARDING_SKIPPED: 'Onboarding skipped',
  ONBOARDING_RESEARCH_CONTINUED_AFTER_WAIT: 'Onboarding research continued after wait',
  ONBOARDING_RESEARCH_COMPLETED: 'Onboarding research completed',
  ONBOARDING_RESEARCH_RETRY_CLICKED: 'Onboarding research retry clicked',
  ONBOARDING_RESEARCH_WEBSITE_UPDATED: 'Onboarding research website updated',
  FIRST_PUBLISH: 'First publish',
  ONBOARDING_LIVE_BUILD_STEP_COMPLETED: 'Onboarding live build step completed',
  ONBOARDING_PREVIEW_OPENED: 'Onboarding preview opened',
  // Campaign Lifecycle
  CAMPAIGN_ACTIVATED: 'Campaign activated',
  CAMPAIGN_DEACTIVATED: 'Campaign deactivated',

  // Split Testing
  SPLIT_TEST_CREATED: 'Split test created',
  SPLIT_TEST_STARTED: 'Split test started',

  // AI / Righty
  RIGHTY_OPENED: 'Righty opened',
  RIGHTY_MESSAGE_SENT: 'Righty message sent',

  // Template Usage
  TEMPLATE_USED: 'Template used',

  // Feature Interaction Events
  PUBLISHED: 'Published snippet',
  PAYWALL_HIT: 'Paywall hit',
  PREVIEW_STARTED: 'Preview started',
  PREVIEW_ENDED: 'Preview ended',
  TEST_MODE_ENABLED: 'Test mode enabled',
  TEST_MODE_DISABLED: 'Test mode disabled',
  SNIPPET_COPIED: 'Snippet copied',
  SNIPPET_INSTALLED: 'Snippet installed',
  EXPORT_INITIATED: 'Export initiated',
  IMPORT_INITIATED: 'Import initiated',
  BULK_ACTION_PERFORMED: 'Bulk action performed',
  SEARCH_PERFORMED: 'Search performed',
  FILTER_APPLIED: 'Filter applied',
  SORT_CHANGED: 'Sort changed',

  // Engagement & Progress Events
  TUTORIAL_STARTED: 'Tutorial started',
  TUTORIAL_COMPLETED: 'Tutorial completed',
  TOOLTIP_VIEWED: 'Tooltip viewed',
  DOCUMENTATION_ACCESSED: 'Documentation accessed',
  SUPPORT_CONTACTED: 'Support contacted',
  FEEDBACK_PROVIDED: 'Feedback provided',
  PROMO_DISMISSED: 'Promo dismissed',
  MILESTONE_REACHED: 'Milestone reached',
  FIRST_CONVERSION: 'First conversion tracked',

  // AI/Perfect Page Events
  AI_SUGGESTION_GENERATED: 'AI suggestion generated',
  AI_SUGGESTION_APPLIED: 'AI suggestion applied',
  AI_SUGGESTION_REJECTED: 'AI suggestion rejected',
  PERFECT_PAGE_SCAN_INITIATED: 'Perfect page scan initiated',
  PERFECT_PAGE_CHANGES_APPLIED: 'Perfect page changes applied',

  // Error & Recovery Events
  ERROR_ENCOUNTERED: 'Error encountered',
  ERROR_RECOVERED: 'Error recovered',
  VALIDATION_FAILED: 'Validation failed',
  API_ERROR: 'API error',

  // Trial & Billing Events
  BILLING_DIALOG_OPENED: 'Billing dialog opened',
  VIEWED_UPGRADE: 'Viewed upgrade'
})

export const recordAnalyticsEvent = (
  event: string,
  params = {},
  route?: Pick<RouteLocationNormalized, 'name' | 'path' | 'query'>
) => {
  const { user } = useAuthStore()
  const { currentTeamPid, currentTeam } = useConfigStore()

  if (!user) {
    return
  }
  if (user.is_admin && !user.owned_teams.find((t) => String(t.pid) === String(currentTeamPid))) {
    return
  }
  const currentPath = route?.path ?? window.location.pathname
  const routeInfo = {
    currentPath,
    currentRouteName: route?.name ?? null,
    currentModule: currentPath.split('/')[1] || 'home',
    queryParams: route?.query ?? Object.fromEntries(new URLSearchParams(window.location.search))
  }

  // Automatically enrich event parameters with contextual data
  const enrichedParams = {
    ...params,
    // Team context
    teamPid: currentTeam?.pid,
    teamName: currentTeam?.name,

    // Session context
    sessionId: window.sessionStorage.getItem('analytics_session_id') || generateSessionId(),
    timestamp: new Date().toISOString(),

    // Page context
    ...routeInfo

    // Browser context
    // userAgent: navigator.userAgent,
    // screenResolution: `${window.screen.width}x${window.screen.height}`,
    // viewportSize: `${window.innerWidth}x${window.innerHeight}`,

    // Feature usage context
    // hasIntegrations: currentTeam?.integrations?.length > 0,
    // hasPublishedConfig: currentTeam?.last_published_at !== null,
    // daysSinceSignup: user.created_at
    //   ? Math.floor(
    //       (Date.now() - new Date(user.created_at).getTime()) /
    //         (1000 * 60 * 60 * 24)
    //     )
    //   : 0,

    // // Trial context
    // isOnTrial: user.subscription_status === 'trialing',
    // trialDaysRemaining: user.trial_ends_at
    //   ? Math.floor(
    //       (new Date(user.trial_ends_at).getTime() - Date.now()) /
    //         (1000 * 60 * 60 * 24)
    //     )
    //   : null
  }

  if (!currentTeamPid) {
    return
  }

  return api.post(`/api/${currentTeamPid}/analytics`, {
    event,
    params: enrichedParams
  })
}

// Helper function to generate or retrieve session ID
function generateSessionId(): string {
  const sessionId = `sess_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`
  window.sessionStorage.setItem('analytics_session_id', sessionId)
  return sessionId
}
